import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Wand2 } from 'lucide-react';

interface WizardConfig {
  standardBays: number;
  specialty: { alignment: boolean; adas: boolean; combo: boolean };
  layout: 'straight' | 'two_rows' | 'u_shape';
  frontParking: number;
  rearParking: number;
}

interface SetupWizardProps {
  open: boolean;
  onComplete: (config: WizardConfig) => void;
}

export function SetupWizard({ open, onComplete }: SetupWizardProps) {
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState<WizardConfig>({
    standardBays: 4,
    specialty: { alignment: false, adas: false, combo: false },
    layout: 'straight',
    frontParking: 0,
    rearParking: 0,
  });

  const steps = [
    // Step 0: Bay count
    <div key="0" className="space-y-3">
      <Label>How many standard lift bays?</Label>
      <Input
        type="number"
        min={1}
        max={20}
        value={config.standardBays}
        onChange={(e) => setConfig(c => ({ ...c, standardBays: Math.max(1, Number(e.target.value) || 1) }))}
      />
    </div>,

    // Step 1: Specialty
    <div key="1" className="space-y-3">
      <Label>Any specialty bays?</Label>
      <div className="space-y-2">
        {(['alignment', 'adas', 'combo'] as const).map(key => (
          <div key={key} className="flex items-center gap-2">
            <Checkbox
              id={key}
              checked={config.specialty[key]}
              onCheckedChange={(v) => setConfig(c => ({
                ...c,
                specialty: { ...c.specialty, [key]: !!v },
              }))}
            />
            <Label htmlFor={key} className="text-sm capitalize">{key === 'combo' ? 'Alignment + ADAS' : key.toUpperCase()}</Label>
          </div>
        ))}
      </div>
    </div>,

    // Step 2: Layout
    <div key="2" className="space-y-3">
      <Label>Layout shape</Label>
      <RadioGroup
        value={config.layout}
        onValueChange={(v) => setConfig(c => ({ ...c, layout: v as WizardConfig['layout'] }))}
      >
        {[
          { value: 'straight', label: 'Straight Line' },
          { value: 'two_rows', label: 'Two Rows' },
          { value: 'u_shape', label: 'U-Shape' },
        ].map(opt => (
          <div key={opt.value} className="flex items-center gap-2">
            <RadioGroupItem value={opt.value} id={opt.value} />
            <Label htmlFor={opt.value} className="text-sm">{opt.label}</Label>
          </div>
        ))}
      </RadioGroup>
    </div>,

    // Step 3: Parking
    <div key="3" className="space-y-3">
      <Label>Parking lots</Label>
      <div className="space-y-2">
        <div>
          <Label className="text-xs text-muted-foreground">Front lot (spaces)</Label>
          <Input
            type="number"
            min={0}
            max={50}
            value={config.frontParking}
            onChange={(e) => setConfig(c => ({ ...c, frontParking: Math.max(0, Number(e.target.value) || 0) }))}
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Rear lot (spaces)</Label>
          <Input
            type="number"
            min={0}
            max={50}
            value={config.rearParking}
            onChange={(e) => setConfig(c => ({ ...c, rearParking: Math.max(0, Number(e.target.value) || 0) }))}
          />
        </div>
      </div>
    </div>,
  ];

  const isLast = step === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            Facility Setup
          </DialogTitle>
          <DialogDescription>
            Step {step + 1} of {steps.length} — we'll generate your layout automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {steps[step]}
        </div>

        {/* Step indicator dots */}
        <div className="flex justify-center gap-1.5">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === step ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {step > 0 && (
            <Button variant="outline" size="sm" onClick={() => setStep(s => s - 1)}>
              Back
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => {
              if (isLast) {
                onComplete(config);
              } else {
                setStep(s => s + 1);
              }
            }}
          >
            {isLast ? 'Generate Layout' : 'Next'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
